import React, { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import ProductCard from "../../../../templates/ecommerce/product/components/common/ProductCard/ProductCard";
import ProductCardSkeleton from "../../../../templates/ecommerce/product/components/common/ProductCard/ProductCardSkeleton";
import MinimalProductCard from "./MinimalProductCard";
import { useGetProductsQuery } from "../../../../templates/ecommerce/product/api/productApi";
import { useAppDispatch } from "../../../../common/hooks/hooks";
import { openProductModal } from "../../../../templates/ecommerce/product/components/common/ProductModal/productModalSlice";
import type {
  GetProductsParams,
  TransformedProduct,
} from "../../../../templates/ecommerce/product/types/productApiTypes";

interface ShowcaseSliderRowProps {
  config: {
    title: string;
    cardType: "default" | "minimal";
    source: "tag" | "featured";
    tagId: number | null;
  };
}

// Helper function to split an array into chunks of a specific size
const chunkArray = (
  array: TransformedProduct[],
  size: number,
): TransformedProduct[][] => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

const ShowcaseSliderRow: React.FC<ShowcaseSliderRowProps> = ({ config }) => {
  const dispatch = useAppDispatch();

  // For the minimal design, we fetch more products since they are dense (e.g. 18 items = 6 slides of 3)
  const productLimit = config.cardType === "minimal" ? 18 : 12;

  const queryArgs: GetProductsParams = useMemo(() => {
    const baseArgs: GetProductsParams = { limit: productLimit };

    if (config.source === "tag" && config.tagId) {
      return { ...baseArgs, tag: [config.tagId] };
    }

    if (config.source === "featured") {
      return { ...baseArgs, featured: true };
    }

    return baseArgs;
  }, [config, productLimit]);

  const shouldSkip = config.source === "tag" && !config.tagId;
  const { data, isLoading } = useGetProductsQuery(queryArgs, {
    skip: shouldSkip,
  });
  const products = data?.products || [];

  const handleQuickView = (productId: number) => {
    dispatch(openProductModal({ productId }));
  };

  if (!isLoading && products.length === 0) {
    return null;
  }

  // --- RENDER LOGIC: DEFAULT CARD ---
  if (config.cardType === "default") {
    return (
      <div className="apex-showcase-row">
        <h3 className="apex-showcase-row__title">{config.title}</h3>
        {isLoading ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "20px",
            }}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <Swiper
            modules={[Autoplay]}
            loop={products.length > 3}
            spaceBetween={20}
            autoplay={{ delay: 3500, disableOnInteraction: true }}
            breakpoints={{
              320: { slidesPerView: 1.5 },
              540: { slidesPerView: 2 },
              768: { slidesPerView: 3 },
              1024: { slidesPerView: 3 },
              1280: { slidesPerView: 4 },
              1600: { slidesPerView: 5 },
            }}
          >
            {products.map((product) => (
              <SwiperSlide key={product.id}>
                <ProductCard
                  product={product}
                  view="grid"
                  onQuickViewClick={handleQuickView}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    );
  }

  // --- RENDER LOGIC: MINIMAL CARD (CHUNKS OF 3) ---
  const productChunks = chunkArray(products, 3);

  return (
    <div className="apex-showcase-row">
      <h3 className="apex-showcase-row__title">{config.title}</h3>
      {isLoading ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "20px",
          }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="apex-minimal-column"
              style={{ minHeight: "300px" }}
            ></div>
          ))}
        </div>
      ) : (
        <Swiper
          modules={[Autoplay]}
          loop={productChunks.length > 2} // Loop if we have more than 2 columns
          spaceBetween={20}
          autoplay={{ delay: 3500, disableOnInteraction: true }}
          breakpoints={{
            // Minimal columns are wider because they contain lists, so we show fewer per view
            320: { slidesPerView: 1.1 },
            540: { slidesPerView: 1.5 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 2 },
            1280: { slidesPerView: 3 },
            1600: { slidesPerView: 3 },
          }}
        >
          {productChunks.map((chunk, chunkIndex) => (
            <SwiperSlide key={chunkIndex}>
              {/* This is the white box containing up to 3 minimal cards */}
              <div className="apex-minimal-column">
                {chunk.map((product) => (
                  <MinimalProductCard key={product.id} product={product} />
                ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
};

export default ShowcaseSliderRow;
